import { Box, Typography, Button, Alert, Card, Chip, Grid, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo } from 'react';
import { InventorySelector } from '../../InventorySelector';
import type { InventorySlotAvailability, AddonAvailability } from '@lov/shared';

export interface SelectedInventoryItem {
  quantity: number;
  pricePerHour: number;
}

interface InventorySelectionSectionProps {
  inventoryAvailability: InventorySlotAvailability[];
  selectedInventory: Map<string, SelectedInventoryItem>;
  selectedStartTime: string | null;
  selectedEndTime: string | null;
  onUpdateQuantity: (venueInventoryId: string, quantity: number) => void;
  onComplete: (data: unknown, summary: string) => void;
  registerContinue?: (callback: () => void) => void;
  selectedAddons: Map<string, number>;
  onUpdateAddonQuantity: (addonItemId: string, quantity: number) => void;
}

/**
 * Builds a human-readable summary of selected inventory items
 */
function buildSummary(
  selectedInventory: Map<string, SelectedInventoryItem>,
  inventoryAvailability: InventorySlotAvailability[]
): string {
  const parts: string[] = [];

  for (const [venueInventoryId, { quantity }] of selectedInventory.entries()) {
    if (quantity <= 0) continue;
    const item = inventoryAvailability.find((inv) => inv.venueInventoryId === venueInventoryId);
    const name = item?.itemName ?? venueInventoryId;
    parts.push(`${name} x${quantity}`);
  }

  return parts.length > 0 ? parts.join(', ') : 'No items selected';
}

/**
 * Returns the total selected quantity across all inventory items
 */
function getTotalSelectedQuantity(selectedInventory: Map<string, SelectedInventoryItem>): number {
  let total = 0;
  for (const { quantity } of selectedInventory.values()) {
    total += quantity;
  }
  return total;
}

/**
 * Gets the first selected venueInventoryId (for use in addons)
 */
export function getFirstSelectedVenueInventoryId(
  selectedInventory: Map<string, SelectedInventoryItem>
): string | undefined {
  for (const [venueInventoryId, { quantity }] of selectedInventory.entries()) {
    if (quantity > 0) return venueInventoryId;
  }
  return undefined;
}

export function InventorySelectionSection({
  inventoryAvailability,
  selectedInventory,
  selectedStartTime,
  selectedEndTime,
  onUpdateQuantity,
  onComplete,
  registerContinue,
  selectedAddons,
  onUpdateAddonQuantity,
}: InventorySelectionSectionProps) {
  const totalSelected = getTotalSelectedQuantity(selectedInventory);

  // Derive addons from the currently selected inventory items
  const addons = useMemo(() => {
    const addonMap = new Map<string, AddonAvailability>();
    for (const [venueInventoryId] of selectedInventory.entries()) {
      const item = inventoryAvailability.find((inv) => inv.venueInventoryId === venueInventoryId);
      if (item?.addons) {
        for (const addon of item.addons) {
          if (!addonMap.has(addon.addonItemId)) {
            addonMap.set(addon.addonItemId, addon);
          }
        }
      }
    }
    return Array.from(addonMap.values());
  }, [selectedInventory, inventoryAvailability]);

  const handleContinue = useCallback(() => {
    // Build the data payload - array of { venueInventoryId, quantity, pricePerHour }
    const inventoryItems = Array.from(selectedInventory.entries())
      .filter(([, { quantity }]) => quantity > 0)
      .map(([venueInventoryId, { quantity, pricePerHour }]) => ({
        venueInventoryId,
        quantity,
        pricePerHour,
      }));

    const summary = buildSummary(selectedInventory, inventoryAvailability);
    onComplete({ inventoryItems }, summary);
  }, [selectedInventory, inventoryAvailability, onComplete]);

  useEffect(() => {
    if (registerContinue) {
      registerContinue(handleContinue);
    }
  }, [registerContinue, handleContinue]);

  if (inventoryAvailability.length === 0) {
    return (
      <Alert severity="info">
        No equipment available for the selected date and time. Please choose a different time slot.
      </Alert>
    );
  }

  // InventorySelector's getSelectedQuantity searches by: selectedInventory.find(i => i.venueInventoryId === inventoryItemId)
  // So we need to put inventoryItemId values in the venueInventoryId field for the lookup to work.
  // Our internal map uses venueInventoryId as the key - we build a bridge array here.
  const inventorySelections = inventoryAvailability
    .map((inv) => {
      const selected = selectedInventory.get(inv.venueInventoryId);
      return {
        // InventorySelector looks up by comparing venueInventoryId against inventoryItemId
        venueInventoryId: inv.inventoryItemId,
        quantity: selected?.quantity ?? 0,
      };
    })
    .filter((s) => s.quantity > 0);

  // InventorySelector calls onUpdateQuantity with inventoryItemId
  const handleUpdateQuantity = (inventoryItemId: string, quantity: number) => {
    const item = inventoryAvailability.find((inv) => inv.inventoryItemId === inventoryItemId);
    if (!item) return;
    onUpdateQuantity(item.venueInventoryId, quantity);
  };

  return (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Select the equipment you want to use for your session
        </Typography>

        <InventorySelector
          inventory={inventoryAvailability}
          selectedInventory={inventorySelections}
          selectedStartTime={selectedStartTime}
          selectedEndTime={selectedEndTime}
          onUpdateQuantity={handleUpdateQuantity}
        />

        {addons.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Add-ons (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enhance your session with additional equipment
            </Typography>
            <Grid container spacing={1.5}>
              {addons.map((addon) => (
                <Grid size={{ xs: 6, sm: 4 }} key={addon.addonItemId}>
                  <CompactAddonCard
                    addon={addon}
                    quantity={selectedAddons.get(addon.addonItemId) ?? 0}
                    onUpdateQuantity={(qty) => onUpdateAddonQuantity(addon.addonItemId, qty)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleContinue}
            size="large"
            disabled={totalSelected === 0}
          >
            {totalSelected > 0 ? 'Continue' : 'Select at least one item'}
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}

interface CompactAddonCardProps {
  addon: AddonAvailability;
  quantity: number;
  onUpdateQuantity: (qty: number) => void;
}

function CompactAddonCard({ addon, quantity, onUpdateQuantity }: CompactAddonCardProps) {
  return (
    <Card variant="outlined" sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {addon.addonName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {addon.pricePerHour != null ? `\u20B9${addon.pricePerHour}/hr` : 'Included'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Chip label={`${addon.availableQuantity} avail`} size="small" color="success" />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={() => onUpdateQuantity(Math.max(0, quantity - 1))}
              disabled={quantity === 0}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
              {quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onUpdateQuantity(Math.min(addon.availableQuantity, quantity + 1))}
              disabled={quantity >= addon.availableQuantity}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
